'use client'

import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { FoodImage } from '@/components/FoodImage'
import {
  RESTAURANT,
  MENU,
  ALL_ITEMS,
  OPENING_HOURS,
  GALLERY,
  formatFCFA,
  type MenuItem,
} from '@/lib/restaurant'

type OrderType = 'emporter' | 'sur-place'

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
}

function scrollToId(id: string): void {
  const el = document.getElementById(id)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// ----------------------------------------------------------------------------
// WhatsApp helper
// ----------------------------------------------------------------------------
function buildWhatsAppLink(message: string): string {
  return `https://wa.me/${RESTAURANT.whatsappNumber}?text=${encodeURIComponent(message)}`
}

// ============================================================================
// NAVBAR
// ============================================================================
interface NavLink {
  label: string
  id: string
}

const NAV_LINKS: NavLink[] = [
  { label: 'Accueil', id: 'accueil' },
  { label: 'Menu', id: 'menu' },
  { label: 'À propos', id: 'apropos' },
  { label: 'Commander', id: 'commande' },
  { label: 'Localisation', id: 'localisation' },
  { label: 'Contact', id: 'contact' },
]

function Navbar(): React.ReactElement {
  const [open, setOpen] = useState(false)

  const go = (id: string): void => {
    setOpen(false)
    scrollToId(id)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-brand-charcoal/90 backdrop-blur-md border-b border-white/10">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <button onClick={() => go('accueil')} className="flex items-center gap-2" aria-label="Tediux — accueil">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-orange to-brand-red flex items-center justify-center text-lg shadow-warm">
            🍽️
          </span>
          <span className="font-display font-extrabold text-xl text-white tracking-tight">
            Tediux
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => go(link.id)}
              className="px-3 py-2 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {link.label}
            </button>
          ))}
          <a
            href={buildWhatsAppLink('Bonjour Tediux 👋, je souhaite passer une commande.')}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-2 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-green-500 hover:bg-green-600 text-white transition-colors shadow-lg"
          >
            <WhatsAppIcon className="w-4 h-4" />
            WhatsApp
          </a>
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-white hover:bg-white/10"
          aria-label="Ouvrir le menu"
          aria-expanded={open}
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {open ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {open && (
        <nav className="md:hidden bg-brand-charcoal border-t border-white/10 px-5 py-4 flex flex-col gap-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => go(link.id)}
              className="text-left px-3 py-3 text-white/85 hover:bg-white/10 rounded-lg font-medium"
            >
              {link.label}
            </button>
          ))}
          <a
            href={buildWhatsAppLink('Bonjour Tediux 👋, je souhaite passer une commande.')}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 font-semibold rounded-lg bg-green-500 text-white"
          >
            <WhatsAppIcon className="w-5 h-5" />
            Commander sur WhatsApp
          </a>
        </nav>
      )}
    </header>
  )
}

// ============================================================================
// HERO
// ============================================================================
interface HeroProps {
  onOrder: () => void
}

function Hero({ onOrder }: HeroProps): React.ReactElement {
  return (
    <section id="accueil" className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* Fond photo appétissant */}
      <div className="absolute inset-0">
        <FoodImage
          src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=1600&q=80"
          alt="Plats savoureux Tediux"
          emoji="🍲"
          className="w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/95 via-brand-charcoal/80 to-brand-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-5 pt-24 pb-16 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.12 } } }}
          className="max-w-2xl"
        >
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-brand-yellow text-sm font-semibold mb-6"
          >
            📍 Tangbé, Calavi — Bénin
          </motion.span>

          <motion.h1
            variants={fadeUp}
            className="font-display font-extrabold text-white text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight mb-5"
          >
            Bienvenue chez{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow via-brand-orange to-brand-red">
              Tediux
            </span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-lg sm:text-xl text-white/85 mb-8 max-w-xl">
            {RESTAURANT.tagline}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollToId('menu')}
              className="px-8 py-4 rounded-2xl font-display font-bold text-brand-charcoal bg-brand-yellow hover:bg-brand-orange hover:text-white transition-colors shadow-warm text-center"
            >
              Voir le menu
            </button>
            <button
              onClick={onOrder}
              className="px-8 py-4 rounded-2xl font-display font-bold text-white bg-brand-red hover:bg-brand-red-dark transition-colors shadow-warm text-center"
            >
              Commander maintenant
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

// ============================================================================
// MENU
// ============================================================================
interface MenuSectionProps {
  onOrderItem: (item: MenuItem) => void
}

function MenuSection({ onOrderItem }: MenuSectionProps): React.ReactElement {
  const [activeCategory, setActiveCategory] = useState<string>('all')

  const visibleCategories = useMemo(() => {
    if (activeCategory === 'all') return MENU
    return MENU.filter((category) => category.id === activeCategory)
  }, [activeCategory])

  return (
    <section id="menu" className="py-20 sm:py-24 bg-brand-cream">
      <div className="max-w-6xl mx-auto px-5">
        <SectionHeading eyebrow="Notre carte" title="Un menu qui donne faim 😋" />

        {/* Filtres catégories */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          <FilterChip
            label="Tout"
            emoji="🍽️"
            active={activeCategory === 'all'}
            onClick={() => setActiveCategory('all')}
          />
          {MENU.map((category) => (
            <FilterChip
              key={category.id}
              label={category.name}
              emoji={category.emoji}
              active={activeCategory === category.id}
              onClick={() => setActiveCategory(category.id)}
            />
          ))}
        </div>

        <div className="space-y-16">
          {visibleCategories.map((category) => (
            <div key={category.id}>
              <h3 className="font-display font-bold text-2xl text-brand-ink mb-6 flex items-center gap-2">
                <span aria-hidden>{category.emoji}</span> {category.name}
              </h3>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {category.items.map((item) => (
                  <MenuCard key={item.id} item={item} onOrder={() => onOrderItem(item)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

interface FilterChipProps {
  label: string
  emoji: string
  active: boolean
  onClick: () => void
}

function FilterChip({ label, emoji, active, onClick }: FilterChipProps): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors border ${
        active
          ? 'bg-brand-red text-white border-brand-red shadow-warm'
          : 'bg-white text-brand-ink border-black/10 hover:border-brand-orange hover:text-brand-orange'
      }`}
    >
      <span aria-hidden className="mr-1">
        {emoji}
      </span>
      {label}
    </button>
  )
}

interface MenuCardProps {
  item: MenuItem
  onOrder: () => void
}

function MenuCard({ item, onOrder }: MenuCardProps): React.ReactElement {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5 }}
      className="group bg-white rounded-3xl overflow-hidden shadow-card border border-black/5 flex flex-col"
    >
      <div className="relative h-48 overflow-hidden">
        <FoodImage
          src={item.image}
          alt={item.name}
          emoji={item.emoji}
          className="w-full h-full transition-transform duration-500 group-hover:scale-110"
        />
        <span className="absolute top-3 right-3 px-3 py-1 rounded-full bg-brand-charcoal/85 text-brand-yellow text-sm font-bold backdrop-blur">
          {formatFCFA(item.price)}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h4 className="font-display font-bold text-lg text-brand-ink mb-1">{item.name}</h4>
        <p className="text-sm text-brand-ink/70 mb-4 flex-1">{item.description}</p>
        <button
          onClick={onOrder}
          className="w-full py-2.5 rounded-xl font-semibold text-white bg-brand-orange hover:bg-brand-red transition-colors"
        >
          Commander
        </button>
      </div>
    </motion.article>
  )
}

// ============================================================================
// À PROPOS
// ============================================================================
function AboutSection(): React.ReactElement {
  return (
    <section id="apropos" className="py-20 sm:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden shadow-warm aspect-[4/3]"
        >
          <FoodImage
            src="https://images.unsplash.com/photo-1552566626-52f8b828add9?w=1000&q=80"
            alt="L'ambiance conviviale de Tediux"
            emoji="🍴"
            className="w-full h-full"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-brand-orange font-bold uppercase tracking-widest text-sm">
            À propos de nous
          </span>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-brand-ink mt-2 mb-5">
            Un petit resto, une grande passion
          </h2>
          <p className="text-brand-ink/75 text-lg leading-relaxed mb-4">
            <strong>Tediux</strong> est un restaurant chaleureux et convivial situé à Tangbé,
            Calavi. Nous préparons chaque jour des plats savoureux, généreux et accessibles,
            avec des produits frais et beaucoup d’amour.
          </p>
          <p className="text-brand-ink/75 text-lg leading-relaxed mb-8">
            Que vous souhaitiez manger sur place dans une ambiance conviviale ou commander
            rapidement à emporter, Tediux vous accueille avec le sourire.
          </p>
          <div className="grid grid-cols-3 gap-4">
            <Stat value="100%" label="Fait maison" />
            <Stat value="6+" label="Catégories" />
            <Stat value="😋" label="Le goût qui revient" />
          </div>
        </motion.div>
      </div>
    </section>
  )
}

interface StatProps {
  value: string
  label: string
}

function Stat({ value, label }: StatProps): React.ReactElement {
  return (
    <div className="text-center p-4 rounded-2xl bg-brand-cream border border-black/5">
      <div className="font-display font-extrabold text-2xl text-brand-red">{value}</div>
      <div className="text-xs text-brand-ink/60 mt-1">{label}</div>
    </div>
  )
}

// ============================================================================
// GALERIE
// ============================================================================
function GallerySection(): React.ReactElement {
  return (
    <section id="galerie" className="py-20 sm:py-24 bg-brand-charcoal">
      <div className="max-w-6xl mx-auto px-5">
        <SectionHeading eyebrow="Galerie" title="Nos plats en images" dark />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {GALLERY.map((src, index) => (
            <motion.div
              key={src}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className={`relative overflow-hidden rounded-2xl ${index === 0 ? 'col-span-2 row-span-2' : ''}`}
            >
              <FoodImage
                src={src}
                alt={`Plat Tediux ${index + 1}`}
                emoji="🍽️"
                className={`w-full ${index === 0 ? 'h-full min-h-[16rem]' : 'h-40 md:h-48'} hover:scale-110 transition-transform duration-500`}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================================================
// COMMANDE (formulaire WhatsApp)
// ============================================================================
interface OrderSectionProps {
  selectedItemId: string
  setSelectedItemId: (id: string) => void
}

function OrderSection({ selectedItemId, setSelectedItemId }: OrderSectionProps): React.ReactElement {
  const [quantity, setQuantity] = useState(1)
  const [instructions, setInstructions] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [orderType, setOrderType] = useState<OrderType>('emporter')

  const selectedItem = useMemo(
    () => ALL_ITEMS.find((item) => item.id === selectedItemId) ?? ALL_ITEMS[0],
    [selectedItemId],
  )

  const total = selectedItem.price * quantity

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    const lines = [
      `🍽️ *Nouvelle commande — Tediux*`,
      ``,
      `👤 Nom : ${name || 'Non précisé'}`,
      `📱 WhatsApp : ${phone || 'Non précisé'}`,
      `🛍️ Type : ${orderType === 'emporter' ? 'À emporter' : 'Sur place'}`,
      ``,
      `🍴 Plat : ${selectedItem.name}`,
      `🔢 Quantité : ${quantity}`,
      `💰 Total : ${formatFCFA(total)}`,
    ]
    if (instructions.trim()) {
      lines.push(``, `📝 Instructions : ${instructions.trim()}`)
    }
    lines.push(``, `Merci ! 🙏`)
    const url = buildWhatsAppLink(lines.join('\n'))
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <section id="commande" className="py-20 sm:py-24 bg-gradient-to-br from-brand-red via-brand-red-dark to-brand-charcoal">
      <div className="max-w-3xl mx-auto px-5">
        <SectionHeading
          eyebrow="Commander"
          title="Passez commande en 30 secondes"
          subtitle="Remplissez le formulaire, puis envoyez directement sur WhatsApp."
          dark
        />

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl shadow-warm p-6 sm:p-8 space-y-5"
        >
          {/* Plat */}
          <div>
            <label htmlFor="dish" className="block text-sm font-semibold text-brand-ink mb-2">
              1. Choisissez votre plat
            </label>
            <select
              id="dish"
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              className="w-full rounded-xl border border-black/15 px-4 py-3 text-brand-ink focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30 outline-none bg-white"
            >
              {MENU.map((category) => (
                <optgroup key={category.id} label={`${category.emoji} ${category.name}`}>
                  {category.items.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} — {formatFCFA(item.price)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Quantité */}
          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-2">2. Quantité</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="w-11 h-11 rounded-xl bg-brand-cream text-brand-ink font-bold text-xl hover:bg-brand-yellow transition-colors"
                aria-label="Diminuer la quantité"
              >
                −
              </button>
              <span className="w-14 text-center font-display font-bold text-xl text-brand-ink">
                {quantity}
              </span>
              <button
                type="button"
                onClick={() => setQuantity((q) => Math.min(50, q + 1))}
                className="w-11 h-11 rounded-xl bg-brand-cream text-brand-ink font-bold text-xl hover:bg-brand-yellow transition-colors"
                aria-label="Augmenter la quantité"
              >
                +
              </button>
              <span className="ml-auto text-sm text-brand-ink/60">
                Total :{' '}
                <span className="font-bold text-brand-red text-base">{formatFCFA(total)}</span>
              </span>
            </div>
          </div>

          {/* Instructions */}
          <div>
            <label htmlFor="instructions" className="block text-sm font-semibold text-brand-ink mb-2">
              3. Instructions particulières (optionnel)
            </label>
            <textarea
              id="instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={2}
              placeholder="Ex : sans oignon, bien piquant, etc."
              className="w-full rounded-xl border border-black/15 px-4 py-3 text-brand-ink focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30 outline-none resize-none"
            />
          </div>

          {/* Nom + Téléphone */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-brand-ink mb-2">
                4. Votre nom
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Votre nom"
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-brand-ink focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30 outline-none"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-brand-ink mb-2">
                5. Numéro WhatsApp
              </label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="Ex : 01 90 00 00 00"
                className="w-full rounded-xl border border-black/15 px-4 py-3 text-brand-ink focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30 outline-none"
              />
            </div>
          </div>

          {/* Type de commande */}
          <div>
            <label className="block text-sm font-semibold text-brand-ink mb-2">
              6. Mode de service
            </label>
            <div className="grid grid-cols-2 gap-3">
              <OrderTypeButton
                active={orderType === 'emporter'}
                onClick={() => setOrderType('emporter')}
                emoji="🛍️"
                label="À emporter"
              />
              <OrderTypeButton
                active={orderType === 'sur-place'}
                onClick={() => setOrderType('sur-place')}
                emoji="🍽️"
                label="Sur place"
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl font-display font-bold text-white bg-green-500 hover:bg-green-600 transition-colors shadow-lg text-lg"
          >
            <WhatsAppIcon className="w-6 h-6" />
            Commander sur WhatsApp
          </button>
        </form>
      </div>
    </section>
  )
}

interface OrderTypeButtonProps {
  active: boolean
  onClick: () => void
  emoji: string
  label: string
}

function OrderTypeButton({ active, onClick, emoji, label }: OrderTypeButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-2 py-3 rounded-xl font-semibold border-2 transition-colors ${
        active
          ? 'bg-brand-orange text-white border-brand-orange'
          : 'bg-white text-brand-ink border-black/15 hover:border-brand-orange'
      }`}
    >
      <span aria-hidden>{emoji}</span>
      {label}
    </button>
  )
}

// ============================================================================
// LOCALISATION + HORAIRES
// ============================================================================
function LocationSection(): React.ReactElement {
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(RESTAURANT.mapsQuery)}&output=embed`

  return (
    <section id="localisation" className="py-20 sm:py-24 bg-brand-cream">
      <div className="max-w-6xl mx-auto px-5">
        <SectionHeading eyebrow="Nous trouver" title="Localisation & horaires" />
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Carte */}
          <div className="rounded-3xl overflow-hidden shadow-card border border-black/5 min-h-[320px]">
            <iframe
              title="Carte Tediux — Tangbé, Calavi"
              src={mapSrc}
              className="w-full h-full min-h-[320px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Infos + horaires */}
          <div className="flex flex-col gap-6">
            <div className="bg-white rounded-3xl p-6 shadow-card border border-black/5">
              <h3 className="font-display font-bold text-xl text-brand-ink mb-4 flex items-center gap-2">
                📍 Adresse
              </h3>
              <p className="text-brand-ink/80 text-lg">
                <strong>Tediux</strong>
                <br />
                {RESTAURANT.address}
              </p>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(RESTAURANT.mapsQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-brand-red font-semibold hover:text-brand-orange"
              >
                Ouvrir dans Google Maps →
              </a>
            </div>

            <div className="bg-white rounded-3xl p-6 shadow-card border border-black/5">
              <h3 className="font-display font-bold text-xl text-brand-ink mb-4 flex items-center gap-2">
                🕒 Nos horaires
              </h3>
              <ul className="divide-y divide-black/5">
                {OPENING_HOURS.map((row) => (
                  <li key={row.day} className="flex items-center justify-between py-2.5">
                    <span className="text-brand-ink/80">{row.day}</span>
                    <span
                      className={`font-semibold ${row.closed ? 'text-brand-red' : 'text-brand-ink'}`}
                    >
                      {row.closed ? 'Fermé' : row.hours}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ============================================================================
// CONTACT
// ============================================================================
function ContactSection(): React.ReactElement {
  return (
    <section id="contact" className="py-20 sm:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-5">
        <SectionHeading eyebrow="Contact" title="Une question ? Écrivez-nous" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <ContactCard emoji="💬" title="WhatsApp" value={RESTAURANT.phoneDisplay} />
          <ContactCard emoji="📞" title="Téléphone" value={RESTAURANT.phoneDisplay} />
          <ContactCard emoji="📍" title="Adresse" value={RESTAURANT.address} />
          <ContactCard emoji="🕒" title="Horaires" value="Tous les jours" />
        </div>

        <div className="text-center">
          <a
            href={buildWhatsAppLink('Bonjour Tediux 👋, j’ai une question.')}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl font-display font-bold text-white bg-green-500 hover:bg-green-600 transition-colors shadow-warm text-lg"
          >
            <WhatsAppIcon className="w-6 h-6" />
            Contacter sur WhatsApp
          </a>
        </div>
      </div>
    </section>
  )
}

interface ContactCardProps {
  emoji: string
  title: string
  value: string
}

function ContactCard({ emoji, title, value }: ContactCardProps): React.ReactElement {
  return (
    <div className="bg-brand-cream rounded-2xl p-5 border border-black/5 text-center">
      <div className="text-3xl mb-2" aria-hidden>
        {emoji}
      </div>
      <div className="font-display font-bold text-brand-ink">{title}</div>
      <div className="text-sm text-brand-ink/70 mt-1 break-words">{value}</div>
    </div>
  )
}

// ============================================================================
// FOOTER
// ============================================================================
function Footer(): React.ReactElement {
  return (
    <footer className="bg-brand-charcoal text-white/80 py-12">
      <div className="max-w-6xl mx-auto px-5 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-orange to-brand-red flex items-center justify-center text-lg">
            🍽️
          </span>
          <span className="font-display font-extrabold text-2xl text-white">Tediux</span>
        </div>
        <p className="font-display font-semibold text-brand-yellow text-lg mb-1">
          Tediux – {RESTAURANT.slogan}
        </p>
        <p className="text-white/60 mb-6">{RESTAURANT.address}</p>
        <p className="text-white/40 text-sm">
          © {new Date().getFullYear()} Tediux. Tous droits réservés.
        </p>
      </div>
    </footer>
  )
}

// ============================================================================
// FLOATING WHATSAPP BUTTON
// ============================================================================
function FloatingWhatsApp(): React.ReactElement {
  return (
    <a
      href={buildWhatsAppLink('Bonjour Tediux 👋, je souhaite passer une commande.')}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-green-500 hover:bg-green-600 text-white font-semibold shadow-2xl animate-float-slow"
      aria-label="Commander sur WhatsApp"
    >
      <WhatsAppIcon className="w-6 h-6" />
      <span className="hidden sm:inline">Commander</span>
    </a>
  )
}

// ============================================================================
// SHARED
// ============================================================================
interface SectionHeadingProps {
  eyebrow: string
  title: string
  subtitle?: string
  dark?: boolean
}

function SectionHeading({ eyebrow, title, subtitle, dark }: SectionHeadingProps): React.ReactElement {
  return (
    <div className="text-center mb-10 sm:mb-12">
      <span className="text-brand-orange font-bold uppercase tracking-widest text-sm">{eyebrow}</span>
      <h2
        className={`font-display font-extrabold text-3xl sm:text-4xl mt-2 ${
          dark ? 'text-white' : 'text-brand-ink'
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-3 max-w-xl mx-auto ${dark ? 'text-white/80' : 'text-brand-ink/70'}`}>
          {subtitle}
        </p>
      )}
    </div>
  )
}

function WhatsAppIcon({ className = '' }: { className?: string }): React.ReactElement {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  )
}

// ============================================================================
// MAIN
// ============================================================================
export function RestaurantSite(): React.ReactElement {
  const [selectedItemId, setSelectedItemId] = useState<string>(ALL_ITEMS[0].id)

  const handleOrderItem = (item: MenuItem): void => {
    setSelectedItemId(item.id)
    scrollToId('commande')
  }

  return (
    <div className="bg-brand-cream text-brand-ink">
      <Navbar />
      <main>
        <Hero onOrder={() => scrollToId('commande')} />
        <MenuSection onOrderItem={handleOrderItem} />
        <AboutSection />
        <GallerySection />
        <OrderSection selectedItemId={selectedItemId} setSelectedItemId={setSelectedItemId} />
        <LocationSection />
        <ContactSection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  )
}
